import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/shared/components';
import type { UserResponse } from '@/shared/api';
import { UserCard } from './UserCard';
import { UserRow } from './UserRow';
import type { UserRowHandlers } from './userRowActions';

interface Props extends UserRowHandlers {
  users: UserResponse[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  currentUserId?: string;
  busyId: string | null;
}

/**
 * Die Benutzerliste: ab genug Breite eine Datentabelle, darunter gestapelte Karten.
 *
 * <p>Die Umschaltung hängt an einer Container-Query und nicht am Fenster. Die Liste sitzt im
 * Einstellungs-Layout, dessen 240-px-Rail ihr auch auf 1440 px nur rund 714 px lässt — ein
 * `md:`-Breakpoint zeigte die Tabelle also genau dort, wo sie nachweislich nicht
 * hineinpasst (Lehre aus Block D).</p>
 *
 * <p>Der Kopf-Schalter wählt genau die Konten dieser Seite, nicht das gesamte Ergebnis: eine
 * Auswahl über Seitengrenzen hinweg behauptete, man habe gesehen, was man auswählt.</p>
 */
export function UsersTable({
  users,
  selectedIds,
  onToggle,
  onToggleAll,
  currentUserId,
  busyId,
  ...handlers
}: Props) {
  const allSelected = users.length > 0 && users.every((u) => selectedIds.has(u.id));
  const someSelected = users.some((u) => selectedIds.has(u.id));

  return (
    <div className="@container">
      <div className="@3xl:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <label className="flex h-11 w-9 cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(node) => {
                      if (node) node.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={onToggleAll}
                    aria-label={
                      allSelected
                        ? 'Auswahl dieser Seite aufheben'
                        : 'Alle Konten dieser Seite auswählen'
                    }
                    className="accent-sealwax h-4 w-4 cursor-pointer"
                  />
                </label>
              </TableHead>
              <TableHead>Benutzer</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Zustand</TableHead>
              <TableHead>Zwei-Faktor</TableHead>
              <TableHead>Zuletzt angemeldet</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Aktionen</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                selected={selectedIds.has(u.id)}
                onToggleSelect={() => onToggle(u.id)}
                currentUserId={currentUserId}
                busy={busyId === u.id}
                {...handlers}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="@3xl:hidden">
        <label className="text-ink-soft mb-3 flex min-h-11 w-fit cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(node) => {
              if (node) node.indeterminate = someSelected && !allSelected;
            }}
            onChange={onToggleAll}
            className="accent-sealwax h-4 w-4 cursor-pointer"
          />
          {allSelected ? 'Auswahl dieser Seite aufheben' : 'Alle Konten dieser Seite auswählen'}
        </label>
        <ul className="grid gap-3">
          {users.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              selected={selectedIds.has(u.id)}
              onToggleSelect={() => onToggle(u.id)}
              currentUserId={currentUserId}
              busy={busyId === u.id}
              {...handlers}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}
